"use client";

import { ForwardedRef, forwardRef, useEffect } from "react"; // Added useEffect

import IconActive from "@/icons/ui/buttons/activeBtn.svg";
import { cn } from "@/utils/cn";

import styles from "./style.module.scss";

export type List = {
  label: string;
  value: string;
  code?: string;
  link?: string;
  symbol?: string;
  isActive?: boolean;
  icon?: React.FC<React.SVGProps<SVGSVGElement>> | undefined;
};

export interface HandleChangeValue {
  value: string;
  label: string;
  link?: string;
}

export type HandleChange = (val: HandleChangeValue) => void;

interface Props {
  list: List[];
  isOpen: boolean;
  handleChange: HandleChange;
  withIcon?: boolean;
  withOverFull?: boolean;
  isOnlySymbol?: boolean;
  type?: "header_desktop" | undefined;
  itemStyles?: string;
  itemIconStyles?: string;
  position?: "top" | "bottom"; // Added position prop
}

const SelectList = forwardRef<HTMLDivElement, Props>(
  (
    {
      list,
      isOpen,
      handleChange,
      withIcon = false,
      withOverFull = false,
      isOnlySymbol = false,
      type,
      itemStyles,
      itemIconStyles = "icon",
      position = "bottom", // Destructured position prop with default
    },
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    useEffect(() => {
      const listElement = ref && typeof ref !== "function" ? ref.current : null;

      // If the list element doesn't exist, do nothing.
      if (!listElement) {
        return;
      }

      if (isOpen) {
        const offsetParent = listElement.offsetParent as HTMLElement;
        // If there's no offset parent, positioning logic cannot be applied.
        if (!offsetParent) {
          return;
        }

        const PADDING = 16; // Minimum distance from viewport edges in pixels.
        const VERTICAL_PADDING = 8; // Minimum vertical padding for the list.

        const adjustPosition = () => {
          // Ensure the list element is still mounted and has an offset parent.
          if (!listElement.offsetParent) {
            return;
          }

          // Horizontal Positioning
          // Reset inline width to allow its natural width to be calculated based on content and CSS.
          listElement.style.width = "";
          let currentListWidth = listElement.offsetWidth;
          const viewportWidth = window.innerWidth;

          // Calculate the maximum allowed width for the list, considering padding.
          const maxAllowedWidth = viewportWidth - 2 * PADDING;
          if (currentListWidth > maxAllowedWidth) {
            // If the list is wider than allowed, constrain its width.
            listElement.style.width = `${maxAllowedWidth}px`;
            currentListWidth = maxAllowedWidth;
          }

          // Get the offset parent's rect for position calculations.
          const parentRect = offsetParent.getBoundingClientRect();

          // Calculate the 'left' style for default horizontal centering relative to its offset parent.
          let targetLeftInParent =
            (offsetParent.offsetWidth - currentListWidth) / 2;

          // Calculate the list's left edge in viewport coordinates if it were centered.
          const listViewportLeftIfCentered =
            parentRect.left + targetLeftInParent;

          // Check for overflow on the left side.
          if (listViewportLeftIfCentered < PADDING) {
            // Adjust 'left' to align with the viewport's left padding.
            targetLeftInParent = PADDING - parentRect.left;
          } else if (
            listViewportLeftIfCentered + currentListWidth >
            viewportWidth - PADDING
          ) {
            // Check for overflow on the right side.
            // Adjust 'left' to align with the viewport's right padding.
            targetLeftInParent =
              viewportWidth - PADDING - currentListWidth - parentRect.left;
          }

          // Apply the calculated 'left' position and override the default transform.
          listElement.style.left = `${targetLeftInParent}px`;
          listElement.style.transform = "translateX(0%)";

          // Vertical Positioning
          const listElementHeight = listElement.offsetHeight;
          const offsetParentHeight = offsetParent.offsetHeight;
          const viewportHeight = window.innerHeight;

          listElement.style.top = ""; // Reset top/bottom before calculating
          listElement.style.bottom = "";

          const canFitAbove = parentRect.top - listElementHeight >= PADDING;
          const canFitBelow =
            parentRect.bottom + listElementHeight <= viewportHeight - PADDING;

          let placeAbove = false;
          let placeBelow = false;

          if (position === "top") {
            if (canFitAbove) {
              placeAbove = true;
            } else if (canFitBelow) {
              placeBelow = true;
            } else {
              // Neither fits perfectly. Stick with preferred direction (top) and adjust to PADDING.
              placeAbove = true;
            }
          } else {
            // position === "bottom"
            if (canFitBelow) {
              placeBelow = true;
            } else if (canFitAbove) {
              placeAbove = true;
            } else {
              // Neither fits perfectly. Stick with preferred direction (bottom) and adjust to PADDING.
              placeBelow = true;
            }
          }

          if (placeAbove) {
            let topValue = -listElementHeight;
            // If placing above makes the list's top go above viewport PADDING
            if (parentRect.top + topValue < PADDING) {
              // Adjust so list's top is at PADDING (relative to viewport)
              // listElement.style.top value is relative to offsetParent.
              // Desired listElement viewport top = PADDING
              // parentRect.top + new_style_top = PADDING
              // new_style_top = PADDING - parentRect.top
              topValue = PADDING - parentRect.top;
            }
            listElement.style.top = `${topValue - VERTICAL_PADDING}px`;
          } else if (placeBelow) {
            let topValue = offsetParentHeight;
            // If placing below makes the list's bottom go below viewport PADDING
            // listElement's viewport bottom = parentRect.top + topValue + listElementHeight
            // (since topValue is offsetParentHeight, listElement's viewport top = parentRect.bottom)
            // So, listElement's viewport bottom = parentRect.bottom + listElementHeight
            if (
              parentRect.bottom + listElementHeight >
              viewportHeight - PADDING
            ) {
              // Adjust so list's bottom is at (viewportHeight - PADDING)
              // Desired listElement viewport top = (viewportHeight - PADDING - listElementHeight)
              // new_style_top = (viewportHeight - PADDING - listElementHeight) - parentRect.top
              topValue =
                viewportHeight - PADDING - listElementHeight - parentRect.top;
            }
            listElement.style.top = `${topValue + VERTICAL_PADDING}px`;
          }
        };

        adjustPosition(); // Call once immediately when the list opens or content changes.

        // Add event listener for window resize to readjust position.
        window.addEventListener("resize", adjustPosition);

        // Cleanup function: remove event listener and reset styles.
        return () => {
          window.removeEventListener("resize", adjustPosition);
          if (listElement) {
            // Check if element still exists for cleanup
            listElement.style.left = ""; // Reset to CSS default
            listElement.style.transform = ""; // Reset to CSS default
            listElement.style.width = ""; // Reset to CSS default
            listElement.style.top = ""; // Reset top
            listElement.style.bottom = ""; // Reset bottom
          }
        };
      } else {
        // If the list is closed, ensure styles are reset to their CSS defaults.
        if (listElement) {
          // Check if element still exists for cleanup
          listElement.style.left = ""; // Reset to CSS default
          listElement.style.transform = ""; // Reset to CSS default
          listElement.style.width = ""; // Reset to CSS default
          listElement.style.top = ""; // Reset top
          listElement.style.bottom = ""; // Reset bottom
        }
      }
    }, [isOpen, ref, list, position]); // Dependencies for the effect, added position

    return (
      <div
        ref={ref}
        className={cn(
          styles.list,
          isOpen ? styles.open : styles.close,
          withOverFull && styles.listOverFull,
          type && styles[`list_${type}`],
        )}
      >
        {list.map((el, index) => (
          <div
            className={cn(
              itemStyles,
              styles.itemContainer,
              !withIcon && styles.positionCenter,
              type && styles[`itemContainer_${type}`],
            )}
            key={index}
            onClick={() => {
              handleChange(el);
            }}
          >
            {withIcon && el?.icon && (
              <el.icon className={cn(itemIconStyles, "shrink-0")} />
            )}
            {(type === "header_desktop" || isOnlySymbol) &&
              el?.symbol &&
              el.symbol}

            <span className={styles.label}>{el?.label}</span>
            {type === "header_desktop" && el?.isActive && (
              <IconActive className={styles.icon_active} />
            )}
          </div>
        ))}
      </div>
    );
  },
);

SelectList.displayName = "SelectList";
export default SelectList;
